import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { CONTACT_EMAIL, DASHBOARD_URL, MOB_EVENT_TYPE_EVENT } from '../config/const.js';
import { createMailtoLink } from '../utils/email.js';
import { mob } from '../utils/logger.js';

const Pricing = () => {
  const [activeTab, setActiveTab] = useState('memory');
  const [showTooltip, setShowTooltip] = useState(false);
  const emailLink = createMailtoLink(CONTACT_EMAIL, '', '');

  // Handle URL hash for direct navigation to Response API
  useEffect(() => {
    if (window.location.hash === '#response-api') {
      setActiveTab('response');
    }
  }, []);

  // Model pricing data for Response API
  const modelPrices = {
    // GPT Models
    "gpt-5-nano": {"input": 0.00005, "output": 0.00040},
    "gpt-5-mini": {"input": 0.00025, "output": 0.00200},
    "gpt-5": {"input": 0.00125, "output": 0.01000},
    "gpt-4.1": {"input": 0.00200, "output": 0.00800},
    "gpt-4.1-mini": {"input": 0.00040, "output": 0.00160},
    "gpt-4o": {"input": 0.00250, "output": 0.01000},
    "gpt-4o-mini": {"input": 0.00015, "output": 0.00060},

    // Gemini Models
    "gemini-2.5-pro": {"input": 0.00125, "output": 0.01000},
    "gemini-2.5-flash": {"input": 0.00030, "output": 0.00250},

    // Claude Models
    "claude-opus-4.1": {"input": 0.01500, "output": 0.07500},
    "claude-sonnet-4": {"input": 0.00300, "output": 0.01500},

    // Grok Models
    "grok-4": {"input": 0.00300, "output": 0.01500},

    // DeepSeek Models
    "deepseek-R1": {"input": 0.00055, "output": 0.00219},
    "deepseek-v3": {"input": 0.00027, "output": 0.00110},
    "deepseek-v3.1": {"input": 0.00055, "output": 0.00165},
    "deepseek-v3.1-thinking": {"input": 0.00055, "output": 0.00165},
    "memU-memory-model": {"input": 0.00060, "output": 0.00200},
  };

  return (
    <>
      <Helmet>
        <title>MemU Pricing - Simple, Transparent Plans for AI Memory Solutions</title>
        <meta name="description" content="Choose the perfect MemU plan for your AI memory needs. Start free and scale as you grow. No hidden fees, no surprises. Free starter plan with 30 memory calls, professional plan with 600 calls." />
        <meta name="keywords" content="memu pricing, ai memory pricing, chatbot memory cost, llm memory plans, memory layer cost, free ai memory" />
        
        {/* Open Graph */}
        <meta property="og:title" content="MemU Pricing - Simple, Transparent Plans for AI Memory Solutions" />
        <meta property="og:description" content="Choose the perfect MemU plan for your AI memory needs. Start free and scale as you grow. No hidden fees, no surprises." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://memu.pro/pricing" />
        <meta property="og:image" content="https://d192tm8h0ep0ud.cloudfront.net/memu.pro/static/memu-logo.png" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="MemU Pricing - Simple, Transparent Plans for AI Memory Solutions" />
        <meta name="twitter:description" content="Choose the perfect MemU plan for your AI memory needs. Start free and scale as you grow. No hidden fees, no surprises." />
        <meta name="twitter:image" content="https://d192tm8h0ep0ud.cloudfront.net/memu.pro/static/memu-logo.png" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "MemU Pricing",
            "description": "MemU pricing plans for AI memory solutions - from free starter to enterprise",
            "url": "https://memu.pro/pricing",
            "mainEntity": {
              "@type": "Product",
              "name": "MemU",
              "description": "AI memory layer for LLM applications",
              "offers": [
                {
                  "@type": "Offer",
                  "name": "Starter",
                  "price": "0",
                  "priceCurrency": "USD",
                  "description": "Free plan with up to 30 memory calls"
                },
                {
                  "@type": "Offer", 
                  "name": "Professional",
                  "price": "0",
                  "priceCurrency": "USD",
                  "description": "Free plan with up to 600 memory calls"
                },
                {
                  "@type": "Offer",
                  "name": "Enterprise", 
                  "description": "Custom pricing for unlimited memory calls"
                }
              ]
            }
          })}
        </script>
      </Helmet>
      
      <div className="page-container">
      <section className="hero">
        <div className="hero-content">
          <h1>Pricing Plans</h1>
          <p>Choose the perfect plan for your AI memory needs.<br />
            Start free and scale as you grow.</p>
        </div>
      </section>

      {/* Tab Navigation */}
      <section className="pricing-tabs">
        <div className="tab-navigation">
          <button 
            className={`tab-button ${activeTab === 'memory' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('memory'); 
              mob('memu_pro_ev_price_page_click_tab', MOB_EVENT_TYPE_EVENT, { tab_name: 'memory' });
            }}
          >
            Memory API
          </button>
          <button 
            className={`tab-button ${activeTab === 'response' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('response'); 
              mob('memu_pro_ev_price_page_click_tab', MOB_EVENT_TYPE_EVENT, { tab_name: 'response' });
            }}
          >
            Response API
          </button>
        </div>
      </section>

      {/* Memory API Tab Content */}
      {activeTab === 'memory' && (
        <>
          <section className="pricing-plans">
            <div className="section-header">
              <h2>Simple, transparent pricing</h2>
              <p>No hidden fees. No surprises. Just powerful AI memory solutions.</p>
            </div>

        <div className="pricing-grid">
          <div className="pricing-card">
            <div>
              <div className="pricing-header">
                <h3>Pro</h3>
                <div className="price" style={{ flexDirection: 'column' }}>
                  <span className="amount-old" style={{ textDecoration: 'line-through', textDecorationColor: '#000', color: '#6b7280' }}>$249/mo</span>
                  <span className="amount">Free</span>
                </div>
                <p style={{ maxWidth: '300px' }}>Perfect for SMB & indie builders.</p>
              </div>
              <div className="pricing-features">
                <ul>
                  <li><i className="fa-solid fa-check"></i> Unlimited memories</li>
                  <li><i className="fa-solid fa-check"></i> Unlimited end users</li>
                  <li><i className="fa-solid fa-check"></i> Unlimited API calls</li>
                  <li><i className="fa-solid fa-check"></i> Custom Categories</li>
                  <li><i className="fa-solid fa-check"></i> Ready-to-Use</li>
                  <li><i className="fa-solid fa-check"></i> Multiple projects support</li>
                  <li><i className="fa-solid fa-check"></i> Community Support</li>
                </ul>
              </div>
            </div>
            <a href={DASHBOARD_URL}
              className="btn btn-outline btn-lg"
              target="_blank"
              rel="noopener noreferrer">
              Get Started
            </a>
          </div>

          <div className="pricing-card">
            <div className="pricing-header">
              <h3>Enterprise</h3>
              <div className="price">
                <span className="amount">Custom</span>
              </div>
              <p style={{ maxWidth: '300px' }}>For large-scale deployments.</p>
            </div>
            <div className="pricing-features">
              <ul>
                <li><i className="fa-solid fa-check"></i><b> All Pro Features</b></li>
                <li><i className="fa-solid fa-check"></i> On-prem deployment</li>
                <li><i className="fa-solid fa-check"></i> Cluster Categories</li>
                <li><i className="fa-solid fa-check"></i> Theory-of-Mind Ability</li>
                <li><i className="fa-solid fa-check"></i> Multimodal Memory</li>
                <li><i className="fa-solid fa-check"></i> Custom Integrations</li>
                <li><i className="fa-solid fa-check"></i> Private Slack Channel</li>
                <li><i className="fa-solid fa-check"></i> Advanced Analytics</li>
                <li><i className="fa-solid fa-check"></i> Audit Logs</li>
              </ul>
            </div>
            <a href={emailLink}
              className="btn btn-outline btn-lg"
              target="_blank"
              rel="noopener noreferrer">
              Contact Sales
            </a>
          </div>
        </div>
      </section>

      <section className="pricing-faq">
        <div className="section-header">
          <h2>Frequently Asked Questions</h2>
        </div>
        <div className="faq-grid">
          <div className="faq-item">
            <h4>How does the memory counting work?</h4>
            <p>Each piece of information stored in MemU counts as one memory. This includes user conversations, preferences, and contextual data.</p>
          </div>
          <div className="faq-item">
            <h4>Can I upgrade or downgrade my plan?</h4>
            <p>Yes, you can change your plan at any time. Changes take effect immediately.</p>
          </div>
          </div>
          </section>
        </>
      )}

      {/* Response API Tab Content */}
      {activeTab === 'response' && (
        <section className="pricing-plans" id="response-api">
          <div className="section-header">
            <h2>Response API Pricing</h2>
            <p>Pay-per-use pricing for AI model responses. Prices are per 1K tokens.</p>
          </div>

          <div className="pricing-table-container">
            <div className="model-pricing-table">
              <div className="table-header">
                <div className="model-name">Model</div>
                <div className="price-input">Input (per 1K tokens)</div>
                <div className="price-output">Output (per 1K tokens)</div>
              </div>

              {/* GPT Models */}
              <div className="model-category">
                <h4>GPT Models</h4>
                {Object.entries(modelPrices)
                  .filter(([model]) => model.startsWith('gpt-'))
                  .map(([model, prices]) => (
                    <div key={model} className="price-row">
                      <div className="model-name">{model}</div>
                      <div className="price-input">${prices.input.toFixed(5)}</div>
                      <div className="price-output">${prices.output.toFixed(5)}</div>
                    </div>
                  ))
                }
              </div>

              {/* Gemini Models */}
              <div className="model-category">
                <h4>Gemini Models</h4>
                {Object.entries(modelPrices)
                  .filter(([model]) => model.startsWith('gemini-'))
                  .map(([model, prices]) => (
                    <div key={model} className="price-row">
                      <div className="model-name">{model}</div>
                      <div className="price-input">${prices.input.toFixed(5)}</div>
                      <div className="price-output">${prices.output.toFixed(5)}</div>
                    </div>
                  ))
                }
              </div>

              {/* Claude Models */}
              <div className="model-category">
                <h4>Claude Models</h4>
                {Object.entries(modelPrices)
                  .filter(([model]) => model.startsWith('claude-'))
                  .map(([model, prices]) => (
                    <div key={model} className="price-row">
                      <div className="model-name">{model}</div>
                      <div className="price-input">${prices.input.toFixed(5)}</div>
                      <div className="price-output">${prices.output.toFixed(5)}</div>
                    </div>
                  ))
                }
              </div>

              {/* Grok Models */}
              <div className="model-category">
                <h4>Grok Models</h4>
                {Object.entries(modelPrices)
                  .filter(([model]) => model.startsWith('grok-'))
                  .map(([model, prices]) => (
                    <div key={model} className="price-row">
                      <div className="model-name">{model}</div>
                      <div className="price-input">${prices.input.toFixed(5)}</div>
                      <div className="price-output">${prices.output.toFixed(5)}</div>
                    </div>
                  ))
                }
              </div>

              {/* DeepSeek Models */}
              <div className="model-category">
                <h4>DeepSeek Models</h4>
                {Object.entries(modelPrices)
                  .filter(([model]) => model.startsWith('deepseek-'))
                  .map(([model, prices]) => (
                    <div key={model} className="price-row">
                      <div className="model-name">{model}</div>
                      <div className="price-input">${prices.input.toFixed(5)}</div>
                      <div className="price-output">${prices.output.toFixed(5)}</div>
                    </div>
                  ))
                }
              </div>

              {/* MemU Model */}
              <div className="model-category">
                <h4>MemU Models 
                  <span 
                    className="info-icon" 
                    style={{
                      marginLeft: '8px',
                      color: '#6b7280',
                      cursor: 'help',
                      fontSize: '14px',
                      opacity: 0.7,
                      transition: 'opacity 0.2s ease',
                      position: 'relative',
                      display: 'inline-block'
                    }}
                    onMouseEnter={(e) => {
                      setShowTooltip(true);
                      e.target.style.opacity = '1';
                    }}
                    onMouseLeave={(e) => {
                      setShowTooltip(false);
                      e.target.style.opacity = '0.7';
                    }}
                  >
                    <i className="fa-solid fa-circle-question"></i>
                    {showTooltip && (
                      <div style={{
                        position: 'absolute',
                        top: '-80px',
                        left: '20px',
                        backgroundColor: '#333',
                        color: 'white',
                        padding: '12px 16px',
                        borderRadius: '6px',
                        fontSize: '13px',
                        maxWidth: '600px',
                        width: '600px',
                        whiteSpace: 'normal',
                        zIndex: 1000,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        lineHeight: '1.5'
                      }}>
                        The memory model is automatically invoked during conversations, but not on every interaction. The frequency of invocation is determined by factors such as context length and time intervals to optimize performance and cost-effectiveness.
                        <div style={{
                          position: 'absolute',
                          bottom: '-5px',
                          left: '10px',
                          width: 0,
                          height: 0,
                          borderLeft: '5px solid transparent',
                          borderRight: '5px solid transparent',
                          borderTop: '5px solid #333'
                        }}></div>
                      </div>
                    )}
                  </span>
                </h4>
                {Object.entries(modelPrices)
                  .filter(([model]) => model.startsWith('memU-'))
                  .map(([model, prices]) => (
                    <div key={model} className="price-row featured">
                      <div className="model-name">{model}</div>
                      <div className="price-input">${prices.input.toFixed(5)}</div>
                      <div className="price-output">${prices.output.toFixed(5)}</div>
                    </div>
                  ))
                }
              </div>
            </div>
          </div>

          {/* Response API FAQ */}
          <div className="response-api-info">
            <div className="section-header">
              <h3>How Response API Pricing Works</h3>
            </div>
            <div className="info-grid">
              <div className="info-item">
                <h4>Token-based Billing</h4>
                <p>You pay only for what you use. Pricing is calculated based on the number of tokens processed for both input and output.</p>
              </div>
              <div className="info-item">
                <h4>No Monthly Fees</h4>
                <p>No subscription required. Start using any model immediately with pay-per-use pricing.</p>
              </div>
              <div className="info-item">
                <h4>Volume Discounts</h4>
                <p>Contact us for volume discounts on high-usage applications.</p>
              </div>
              <div className="faq-item">
                <h4>How does the memory model invocation work?</h4>
                <p>The memory model is automatically invoked during conversations, but not on every interaction. The frequency of invocation is determined by factors such as context length and time intervals to optimize performance and cost-effectiveness.</p>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
    </>
  );
};

export default Pricing;